/**
 * "Save as Skill…" modal. Validates frontmatter via lib/graph/skills,
 * calls publishFromPrompt to record the new skill in Convex, then
 * triggers a SharePoint write via the passed writeSkill helper (the
 * caller wires audit + Convex updateExternalRef so this component
 * stays React-only).
 *
 * Per feedback_skills_no_client_data: warns if the body appears to
 * contain client-specific data (email, domain, filled placeholder).
 */
import { useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import { validateFrontmatter, type SkillFrontmatterError } from '../../lib/graph/skills';

interface SaveAsSkillDialogProps {
  initialBody: string;
  onSaved: (args: { skillId: string; versionNumber: number; name: string }) => void;
  onCancel: () => void;
}

const CLIENT_DATA_HEURISTICS: Array<{ pattern: RegExp; hint: string }> = [
  { pattern: /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/, hint: 'email address' },
  { pattern: /\b(?:Inc|LLC|Corp|Ltd|GmbH)\.?\b/, hint: 'company suffix' },
  { pattern: /\[ClientName\]/i, hint: 'unfilled "[ClientName]" placeholder' },
];

export function SaveAsSkillDialog({ initialBody, onSaved, onCancel }: SaveAsSkillDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState(initialBody);
  const [errors, setErrors] = useState<SkillFrontmatterError[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [collisionName, setCollisionName] = useState<string | null>(null);
  const publish = useMutation(api.skills.publishFromPrompt);

  const clientDataHints = CLIENT_DATA_HEURISTICS.filter((h) => h.pattern.test(body)).map(
    (h) => h.hint,
  );

  async function handleSubmit(): Promise<void> {
    setServerError(null);
    setCollisionName(null);
    const fmOrErrors = validateFrontmatter({ name, description });
    if (Array.isArray(fmOrErrors)) {
      setErrors(fmOrErrors);
      return;
    }
    setErrors([]);
    setSubmitting(true);
    try {
      const result = await publish({
        name,
        description,
        scope: 'firm',
        body,
        frontmatter: { name, description },
      });
      onSaved({
        skillId: String(result.skillId),
        versionNumber: result.versionNumber,
        name,
      });
    } catch (err) {
      const message = (err as Error).message;
      const collisionMatch = message.match(/A skill named "([^"]+)" already exists/);
      if (collisionMatch) {
        setCollisionName(collisionMatch[1] ?? name);
      } else {
        setServerError(message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={onCancel}
      className="fixed inset-0 flex items-center justify-center bg-black/30 z-[10000]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[480px] bg-[#ece9d8] shadow-[2px_2px_0_#000]"
        style={{
          borderStyle: 'solid',
          borderWidth: 2,
        }}
        data-testid={testIds.skills.saveDialogRoot}
      >
        <div
          className="flex items-center justify-between font-bold text-[12px] text-white px-1.5 py-[3px]"
          style={{
            background: 'linear-gradient(90deg, var(--xp-selection), #1a4ab8)',
            fontFamily: "var(--xp-font-body)",
          }}
        >
          <span>Save as Skill…</span>
          <button
            data-testid={testIds.skills.saveDialogCancel}
            onClick={onCancel}
            className="bg-[#ece9d8] border-2 border-solid border-t-[#dfdfdf] border-l-[#dfdfdf] border-b-[#808080] border-r-[#808080] text-black font-bold text-[12px] leading-none px-1 cursor-pointer"
          >
            X
          </button>
        </div>

        <div className="p-2 space-y-2 text-[11px] text-black font-[MS_Sans_Serif,Tahoma,Geneva,sans-serif]">
          <label className="block">
            <span className="font-bold">Name</span>
            <input
              type="text"
              data-testid={testIds.skills.saveDialogName}
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              className="w-full text-[11px] px-1 py-0.5 mt-0.5 bg-white text-black border-2 border-solid border-t-[#808080] border-l-[#808080] border-b-[#dfdfdf] border-r-[#dfdfdf]"
              placeholder="nda-redline"
            />
          </label>
          <label className="block">
            <span className="font-bold">Description</span>
            <textarea
              data-testid={testIds.skills.saveDialogDescription}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full text-[11px] px-1 py-0.5 mt-0.5 bg-white text-black border-2 border-solid border-t-[#808080] border-l-[#808080] border-b-[#dfdfdf] border-r-[#dfdfdf]"
              placeholder="What this skill does and when to use it"
            />
          </label>
          <label className="block">
            <span className="font-bold">Body</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              className="w-full text-[11px] px-1 py-0.5 mt-0.5 bg-white text-black font-mono border-2 border-solid border-t-[#808080] border-l-[#808080] border-b-[#dfdfdf] border-r-[#dfdfdf]"
            />
          </label>

          {clientDataHints.length > 0 && (
            <div className="text-[11px] text-black bg-yellow-200 px-1.5 py-1 border border-solid border-yellow-700">
              <strong>Possible client data detected:</strong>{' '}
              {clientDataHints.join(', ')}. Skills must not contain client-
              specific examples; route client data through the Client Genome at
              runtime instead.
            </div>
          )}

          {errors.length > 0 && (
            <div
              data-testid={testIds.skills.saveDialogError}
              className="text-[11px] text-white bg-red-700 px-1.5 py-1"
            >
              {errors.map((e, i) => (
                <div key={i}>
                  <strong>{e.field}:</strong> {e.reason}
                </div>
              ))}
            </div>
          )}
          {collisionName && (
            <div
              data-testid={testIds.skills.saveDialogCollision}
              className="text-[11px] text-black bg-yellow-200 px-1.5 py-1 border border-solid border-yellow-700"
            >
              A Skill named <strong>{collisionName}</strong> already exists in the firm library.
              Pick a different name, or open the existing Skill to save a new version.
            </div>
          )}
          {serverError && (
            <div className="text-[11px] text-white bg-red-700 px-1.5 py-1">{serverError}</div>
          )}

          <div className="flex justify-end gap-1 pt-1">
            <button
              data-testid={testIds.skills.saveDialogCancel + '-bottom'}
              onClick={onCancel}
              className="text-[11px] px-3 py-0.5 cursor-pointer bg-[#ece9d8] text-black border-2 border-solid border-t-[#dfdfdf] border-l-[#dfdfdf] border-b-[#808080] border-r-[#808080]"
            >
              Cancel
            </button>
            <button
              data-testid={testIds.skills.saveDialogSubmit}
              disabled={submitting}
              onClick={handleSubmit}
              className="text-[11px] px-3 py-0.5 cursor-pointer bg-[#ece9d8] text-black border-2 border-solid border-t-[#dfdfdf] border-l-[#dfdfdf] border-b-[#808080] border-r-[#808080] disabled:opacity-60"
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
